import { Button, Flex, Layout, Menu, MenuProps, Space, Drawer, Divider, Modal } from "antd";
import { WebDPrivilege } from "api/services/models/WebDUser";
import { ReactComponent as TDLogo } from "Icons/transdyne-01.svg";
import { ReactComponent as BookSavedIcon } from "Icons/vuesax/book-saved.svg";
import { ReactComponent as BookSavedActiveIcon } from "Icons/vuesax/book-saved-active.svg";
import { ReactComponent as ReportsIcon } from "Icons/vuesax/graph.svg";
import { ReactComponent as ReportsActiveIcon } from "Icons/vuesax/graph-active.svg";
import { ReactComponent as MessagesIcon } from "Icons/vuesax/messages-3.svg";
import { ReactComponent as MessagesActiveIcon } from "Icons/vuesax/messages-3-active.svg";
import { ReactComponent as SettingsIcon } from "Icons/vuesax/setting-2.svg";
import { ReactComponent as SettingsActiveIcon } from "Icons/vuesax/setting-2-active.svg";
import { ReactComponent as SimCardIcon } from "Icons/vuesax/simcard.svg";
import { ReactComponent as SimCardActiveIcon } from "Icons/vuesax/simcard-active.svg";
import { ReactComponent as JobsIcon } from "Icons/vuesax/subtitle.svg";
import { ReactComponent as JobsActiveIcon } from "Icons/vuesax/subtitle-active.svg";
import { ReactComponent as SideBarLeft } from "Icons/vuesax/sidebar-left.svg";
import { ReactComponent as SideBarRight } from "Icons/vuesax/sidebar-right.svg";
import { ReactComponent as AddIcon } from "Icons/vuesax/add.svg";
import { ReactComponent as LogoutIcon } from "Icons/vuesax/logout.svg";
import { ReactComponent as PeopleIcon } from "Icons/vuesax/profile-circle.svg";
import { ReactNode, useMemo, useState, useEffect, useContext, createContext } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import AccountPicker from "./AccountPicker";
const packageJson = require("../../package.json");

const currentVersion = packageJson.version;
const { Header, Sider, Content } = Layout;

import { useTDAuthentication } from "auth/AuthenticationProvider";
import MessageBadge from "./messages/MessageBadge";
import UserAvatar from "./UserAvatar";
import MenuItem from "antd/es/menu/MenuItem";

type MenuItem = Required<MenuProps>["items"][number] & { visible?: boolean };

const LayoutContext = createContext({
  isSmallScreen: false,
  toggleDrawer: () => {},
});

export const useLayoutContext = () => useContext(LayoutContext);

const MainContainer = ({ children }: { children: ReactNode }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const [selectedNavItem, setSelectedNavItem] = useState("");
  const { logoutUser, webDUser } = useTDAuthentication(); 
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [fromApp, setFromApp] = useState(false);
  const [isDrawerVisible, setDrawerVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchParams] = useSearchParams();

  const toggleDrawer = () => {
    setDrawerVisible(!isDrawerVisible);
  };

  const onLogout = () => {
    logoutUser?.(); 
    sessionStorage.removeItem("recoveryEmailPrompted");
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768); 
    };
    handleResize(); 
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const mobileUserAgent = searchParams.get('v');
    if(mobileUserAgent == "1") setFromApp(true);
  }, [searchParams]);

  const navItemsMap: { [key: string]: string } = {
    "/jobs": "jobs",
    "/job-summary": "job-summary",
    "/fax-summary": "fax-summary",
    "/messages": "messages",
    "/reports": "reports",
    "/settings": "settings",
    "/guides/vscribeplus-d": "guides/vscribeplus-d",
    "/guides/vscribeplus-v": "guides/vscribeplus-v",
  };
  
  useEffect(() => {
    const path = location.pathname;
    const navItem = navItemsMap[path] || "";
    setSelectedNavItem(navItem);
  }, [location.pathname]);

  const handleSettingsClick = () => {
    setDrawerVisible(false); 
    setIsModalVisible(true); 
  };

  const handleModalClose = () => {
    setIsModalVisible(false); 
  };

  const navItems: MenuItem[] = useMemo(
    () => [
      {
        key: "/jobs",
        label: <Link to="/jobs">Jobs</Link>,
        style: { fontWeight: selectedNavItem === "jobs" ? "bold" : "500"},
        icon: selectedNavItem==="jobs"? <JobsActiveIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />:<JobsIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />,
        visible: webDUser?.hasAnyPrivilege([
          WebDPrivilege.ViewInbox,
          WebDPrivilege.ViewQCHold,
          WebDPrivilege.ViewArchive
        ])
      },
      {
        key: "/job-summary",
        label: <Link to="/job-summary">Job Summary</Link>,
        style: { fontWeight: selectedNavItem === "job-summary" ? "bold" : "500"},
        icon: selectedNavItem==="job-summary"? <BookSavedActiveIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />: <BookSavedIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />,
        visible: webDUser?.hasPrivilege(WebDPrivilege.ViewJobSummary)
      },
      {
        key: "/fax-summary",
        label: <Link to="/fax-summary">Fax Summary</Link>,
        style: { fontWeight: selectedNavItem === "fax-summary" ? "bold" : "500"},
        icon: selectedNavItem==="fax-summary"? <SimCardActiveIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />: <SimCardIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />,
        visible: webDUser?.hasPrivilege(WebDPrivilege.ViewFaxSummary)
      },
      {
        key: "/messages",
        label: <Link to="/messages">Messages</Link>,
        style: { fontWeight: selectedNavItem === "messages" ? "bold" : "500"},
        icon: selectedNavItem==="messages"? <MessagesActiveIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />: <MessagesIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />,
        visible: true
      },
      {
        key: "/reports",
        label: <Link to="/reports">Reports</Link>,
        style: { fontWeight: selectedNavItem === "reports" ? "bold" : "500"},
        icon: selectedNavItem==="reports"? <ReportsActiveIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />: <ReportsIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />,
        visible: webDUser?.hasPrivilege(WebDPrivilege.ManageReports)
      },
      {
        key: "/settings",
        label: <Link to="/settings">Settings</Link>,
        style: { fontWeight: selectedNavItem === "settings" ? "bold" : "500"},
        icon: selectedNavItem==="settings"? <SettingsActiveIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />: <SettingsIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />,
        visible: webDUser?.hasAnyPrivilege([
          WebDPrivilege.ManageAccount,
          WebDPrivilege.ManageLocations,
          WebDPrivilege.ManageContacts,
          WebDPrivilege.EditUser,
          WebDPrivilege.ManageAccountRoles
        ])
      },
      {
        key: "/account",
        label: <Link to="#" onClick={handleSettingsClick}>Select Account</Link>,
        style: { fontWeight: selectedNavItem === "account" ? "bold" : "500"},
        icon: <PeopleIcon width={isSmallScreen? "1.5rem":"1.25rem"} height={isSmallScreen? "1.5rem":"1.25rem"} />,
        visible: isSmallScreen && (!webDUser || "TD" == webDUser.user.accountId)
      }
    ],
    [selectedNavItem, isSmallScreen, webDUser]
  );

  return (
    <LayoutContext.Provider value={{ toggleDrawer, isSmallScreen }}>
      {!isSmallScreen ? (
        <Layout>
          <Header className="main-header-warpper">
            <TDLogo
              className="company-logo"
              width={"9.375rem"}
              height={"1.25rem"}
              style={{ marginLeft: "1.5rem" }}
            />
            <Space
              direction="horizontal"
              size={"middle"}
              align="center"
              style={{ alignItems: "center" }}>
              <AccountPicker />
              <MessageBadge />
              <UserAvatar />
            </Space>
          </Header>
          <Content>
            <Layout>
              {!fromApp && <Sider
                theme="light"
                width={"11.625rem"}
                collapsedWidth={"3.25rem"}
                collapsible={true}
                collapsed={collapsed}
                trigger={null}
                className="main-menu">
                <Flex justify="flex-end">
                  <Button
                    type="text"
                    icon={collapsed ? <SideBarRight /> : <SideBarLeft />}
                    onClick={() => setCollapsed(!collapsed)}
                    className="menu-trigger-button"
                  />
                </Flex>

                <Menu
                  mode="inline"
                  style={{ height: "100%", borderRight: 0 }}
                  items={navItems.filter(i => i.visible).map(({ visible, ...rest }) => rest)}
                  selectedKeys={[location.pathname.toLowerCase()]}
                />
              </Sider>}
              <Content>
                <div className="main-content-wrapper">{children}</div>
              </Content>
            </Layout>
          </Content>
        </Layout>
      ) : (
        <Layout>
          <Content className={isDrawerVisible ? "blur-content" : ""}>
            <div className="main-content-wrapper-mobile">{children}</div>
          </Content>
          {!fromApp && <Drawer
            placement="left"
            styles={{
              body: {
                padding: "0",
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column"
              }
            }}
            closable={true}
            onClose={toggleDrawer}
            open={isDrawerVisible}
            width={300}
            title={<TDLogo width={"9.375rem"} height={"1.25rem"} />}
            closeIcon={
              <div>
                <AddIcon
                  className="close-icon"
                  style={{ color: "#121212", verticalAlign: "initial", margin: "0 0 0 3rem" }}
                />
              </div>
            }
            className="mobile-drawer">
            <Menu
              mode="inline"
              style={{ borderRight: 0 }}
              items={navItems.filter(i => i.visible).map(({ visible, ...rest }) => rest)}
              selectedKeys={[location.pathname.toLowerCase()]}
              className="main-menu"
            />
            <div
              className="logout-item"
              style={{
                padding: "1rem",
                textAlign: "left",
                alignItems: "center",
                display: "flex",
                flexDirection: "column"
              }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                  marginLeft: "1.5rem",
                  alignItems: "center"
                }}>
                <LogoutIcon
                  width={"1.5rem"}
                  height={"1.5rem"}
                  style={{ marginRight: ".75rem" }}
                />
                <span
                  style={{ cursor: "pointer" }}
                  onClick={onLogout}>
                  Logout
                </span>
              </div>
              <Divider />
              <span className="version">{currentVersion}</span>
            </div>
          </Drawer>}
          <Modal
            title="Select Account"
            visible={isModalVisible}
            onCancel={handleModalClose}
            footer={null} // Customize footer if needed
          >
            <AccountPicker />
          </Modal>
        </Layout>
      )}
    </LayoutContext.Provider>
  );
};

export default MainContainer;
